import { memo, useCallback, useState, useMemo } from 'react';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';
import { type FetchBaseQueryError } from '@reduxjs/toolkit/query';
import {
    useListGradersQuery,
    useCreateClaimMutation,
    useDeleteClaimMutation,
    type Grader,
    type ProjectSubmission,
    type ClaimResponse,
    type ProjectIdentifierAutoAssignedInfo,
} from '../api';
import { renderGraderOption, getGraderLabel, filterGraderOptions } from '../shared';

/**
 * Extracts a user-friendly error message from an RTK Query error.
 * The API returns errors in the format { message: "error text" }.
 */
function getErrorMessage(error: unknown): string {
    // RTK Query FetchBaseQueryError with data
    if (error && typeof error === 'object' && 'data' in error) {
        const fetchError = error as FetchBaseQueryError;
        if (fetchError.data && typeof fetchError.data === 'object' && 'message' in fetchError.data) {
            return (fetchError.data as { message: string }).message;
        }
    }
    // Fallback for unexpected error formats
    return 'Failed to update grader';
}

interface GraderAssignmentCellProps {
    submission: ProjectSubmission;
    onAssigned?: () => void;
    onProjectIdentifierAutoAssigned?: (info: ProjectIdentifierAutoAssignedInfo) => void;
}

// oxlint-disable-next-line max-lines-per-function
function GraderAssignmentCellComponent({
    submission,
    onAssigned,
    onProjectIdentifierAutoAssigned,
}: GraderAssignmentCellProps) {
    const { data: gradersData, isLoading: isLoadingGraders } = useListGradersQuery();
    const [createClaim, { isLoading: isCreatingClaim }] = useCreateClaimMutation();
    const [deleteClaim, { isLoading: isDeletingClaim }] = useDeleteClaimMutation();
    const [error, setError] = useState<string | null>(null);

    const graders = useMemo(() => gradersData?.graders ?? [], [gradersData?.graders]);
    const isLoading = isLoadingGraders || isCreatingClaim || isDeletingClaim;

    // Use gradingStatus from the backend which correctly accounts for whether
    // the project requires similarity and originality checking
    const isClaimable = submission.gradingStatus !== 'awaiting_similarity_and_originality';

    // Find currently assigned grader in the list
    const currentGrader = useMemo(() => {
        if (!submission.assignedStatus) return null;
        return graders.find(g => g.id === submission.assignedStatus?.graderId) ?? null;
    }, [submission.assignedStatus, graders]);

    const handleGraderSelect = useCallback(
        async (_event: unknown, grader: Grader | null) => {
            setError(null);

            try {
                // If clearing selection and there's an existing claim, delete it
                if (!grader && submission.assignedStatus?.claimId) {
                    await deleteClaim(submission.assignedStatus.claimId).unwrap();
                    onAssigned?.();
                    return;
                }

                // If selecting a grader
                if (grader) {
                    // If there's an existing claim, delete it first
                    if (submission.assignedStatus?.claimId) {
                        await deleteClaim(submission.assignedStatus.claimId).unwrap();
                    }
                    // Create new claim - admin_assignment bypasses the per-user claim limit
                    const response: ClaimResponse = await createClaim({
                        project_submission_id: submission.id,
                        grader_id: grader.id,
                        admin_assignment: true,
                    }).unwrap();

                    // Check if project identifier was auto-assigned
                    if (response.projectIdentifierAutoAssigned && response.requirementIdentifier) {
                        onProjectIdentifierAutoAssigned?.({
                            graderName: grader.name,
                            requirementIdentifier: response.requirementIdentifier,
                        });
                    }

                    onAssigned?.();
                }
            } catch (err) {
                setError(getErrorMessage(err));
            }
        },
        [
            submission.id,
            submission.assignedStatus,
            createClaim,
            deleteClaim,
            onAssigned,
            onProjectIdentifierAutoAssigned,
        ],
    );

    // If not claimable (S&O not cleared), show disabled state
    if (!isClaimable) {
        return <span style={{ color: '#9ca3af' }}>Awaiting S&O</span>;
    }

    // If graded, show disabled state with grader name if a claim is still associated,
    // otherwise fall back to "Graded". Graded submissions don't appear in My Grading Tasks
    // so changing grader assignment is not meaningful.
    if (submission.gradingStatus === 'graded') {
        const displayText = submission.assignedStatus?.graderName ?? 'Graded';
        return <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>{displayText}</span>;
    }

    return (
        <Autocomplete
            size="small"
            fullWidth
            sx={{
                minWidth: 160,
                // Suppress .front-royal-form-container border styles on native inputs
                '& input': {
                    border: 'none !important',
                    boxShadow: 'none !important',
                    padding: 'inherit !important',
                },
                // Override MUI's default width: 0 on input to prevent text truncation
                '& .MuiAutocomplete-input': {
                    width: 'auto !important',
                    minWidth: '80px !important',
                },
                // Ensure the selected value text is not truncated
                '& .MuiAutocomplete-inputRoot': {
                    flexWrap: 'nowrap',
                },
            }}
            options={graders}
            value={currentGrader}
            getOptionLabel={getGraderLabel}
            filterOptions={filterGraderOptions}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            loading={isLoading}
            disabled={isLoading}
            onChange={handleGraderSelect}
            renderInput={params => (
                <TextField
                    {...params}
                    placeholder="Assign grader..."
                    error={!!error}
                    helperText={error}
                    slotProps={{
                        ...params.slotProps,
                        input: {
                            ...params.slotProps.input,
                            endAdornment: (
                                <>
                                    {isLoading && <CircularProgress size={16} />}
                                    {params.slotProps.input.endAdornment}
                                </>
                            ),
                        },
                    }}
                />
            )}
            renderOption={renderGraderOption}
        />
    );
}

export const GraderAssignmentCell = memo(GraderAssignmentCellComponent);
